import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useInstantPromoSync } from './useInstantPromoSync';

interface Promo {
  id: string;
  nome: string;
  objetivo: string;
  title: string;
  url: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  duration: number;
  interval: number;
  Typepromo: string;
  screen_ids: string[];
  active: boolean;
  user_id: string;
  campaign_id?: string; // Adicionar campaign_id
  Solicitar?: string | string[]; // Pode ser array ou string, depende do backend
  'Modal visível'?: 'HIDDEN' | 'TEMPORARY' | 'PERMANENT' | 'LINKED'; // Campo para modal com 4 estados
  selected_media_ids?: string[] | string; // Para modo LINKED
}

interface UsePromotionsHybridReturn {
  activePromo: Promo | null;
  showPromoModal: boolean;
  closePromoModal: () => void;
  forceCheckPromotions: () => void;
  clearPromoHistory: (promoId?: string) => void;
  isLoading: boolean;
}

export const usePromotionsHybrid = (
  screenId?: string,
  userId?: string,
  filteredMedias?: any[],
  currentMediaIndex?: number
): UsePromotionsHybridReturn => {
  const [activePromo, setActivePromo] = useState<Promo | null>(null);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoTimerActive, setPromoTimerActive] = useState(false);
  const [lastCheck, setLastCheck] = useState<Date>(new Date());
  const [promoHistory, setPromoHistory] = useState<Map<string, Date>>(new Map()); // Histórico de exibições
  const [isRealtimeUpdate, setIsRealtimeUpdate] = useState(false); // Flag para bypass de intervalo
  const [promoVirtualDOM, setPromoVirtualDOM] = useState<Map<string, any>>(new Map()); // Virtual DOM para promoções

  // Sistema de sincronização instantânea
  const { syncPromotions } = useInstantPromoSync({
    userId: userId || '',
    screenId: screenId || '',
    onPromoChanged: () => {
      console.log('🚀 [INSTANT SYNC] Mudanças detectadas - forçando verificação imediata');
      setIsRealtimeUpdate(true);
      checkPromotions();
      setTimeout(() => setIsRealtimeUpdate(false), 1000);
    }
  });

  // Converts HH:MM or HH:MM:SS to total seconds
  const timeToSeconds = (timeStr: string): number => {
    const parts = timeStr.split(':').map(Number);
    const hours = parts[0] || 0;
    const minutes = parts[1] || 0;
    const seconds = parts[2] || 0;
    return hours * 3600 + minutes * 60 + seconds;
  };

  const isTimeInRange = (startTime: string, endTime: string): boolean => {
    const now = new Date();
    const currentSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    const startSeconds = timeToSeconds(startTime);
    const endSeconds = timeToSeconds(endTime);

    const currentTimeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;

    console.log(`🕐 [PROMO TIME] Verificação de horário (precisão de segundos):`, {
      currentTime: currentTimeStr,
      startTime,
      endTime,
      currentSeconds,
      startSeconds,
      endSeconds
    });

    // Se o período cruza a meia-noite (ex: 23:00:00 - 01:00:00)
    if (startSeconds > endSeconds) {
      const result = currentSeconds >= startSeconds || currentSeconds <= endSeconds;
      console.log(`🌙 [PROMO TIME] Período cruza meia-noite - resultado: ${result}`);
      return result;
    }

    // Período normal (ex: 09:00:00 - 17:00:00)
    const result = currentSeconds >= startSeconds && currentSeconds <= endSeconds;
    console.log(`☀️ [PROMO TIME] Período normal - resultado: ${result}`, {
      inRange: result,
      needed: `${currentSeconds} >= ${startSeconds} && ${currentSeconds} <= ${endSeconds}`
    });
    return result;
  };

  const isDateInRange = (startDate: string, endDate: string): boolean => {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`; // YYYY-MM-DD (local)

    const result = today >= startDate && today <= endDate;

    console.log(`📅 [PROMO DATE] Verificação de data (local):`, {
      today,
      startDate,
      endDate,
      todayIsAfterStart: today >= startDate,
      todayIsBeforeEnd: today <= endDate,
      finalResult: result
    });

    return result;
  };

  // Sistema Virtual DOM para promoções - detecta mudanças instantaneamente
  const updatePromoVirtualDOM = useCallback((promo: Promo) => {
    const promoSnapshot = {
      id: promo.id,
      nome: promo.nome,
      title: promo.title,
      modalStatus: promo['Modal visível'],
      active: promo.active,
      start_time: promo.start_time,
      end_time: promo.end_time,
      interval: promo.interval,
      duration: promo.duration,
      start_date: promo.start_date,
      end_date: promo.end_date,
      Typepromo: promo.Typepromo,
      timestamp: Date.now()
    };

    setPromoVirtualDOM(prev => {
      const oldSnapshot = prev.get(promo.id);
      const newMap = new Map(prev);
      newMap.set(promo.id, promoSnapshot);

      // Detectar mudanças
      if (oldSnapshot) {
        const hasChanges = (
          oldSnapshot.modalStatus !== promoSnapshot.modalStatus ||
          oldSnapshot.active !== promoSnapshot.active ||
          oldSnapshot.start_time !== promoSnapshot.start_time ||
          oldSnapshot.end_time !== promoSnapshot.end_time ||
          oldSnapshot.interval !== promoSnapshot.interval ||
          oldSnapshot.duration !== promoSnapshot.duration ||
          oldSnapshot.title !== promoSnapshot.title ||
          oldSnapshot.start_date !== promoSnapshot.start_date ||
          oldSnapshot.end_date !== promoSnapshot.end_date ||
          oldSnapshot.Typepromo !== promoSnapshot.Typepromo
        );

        if (hasChanges) {
          console.log(`🔄 [VIRTUAL DOM] Mudanças detectadas para ${promo.nome}:`, {
            old: oldSnapshot,
            new: promoSnapshot,
            changes: {
              modalStatus: oldSnapshot.modalStatus !== promoSnapshot.modalStatus ? `${oldSnapshot.modalStatus} → ${promoSnapshot.modalStatus}` : 'unchanged',
              active: oldSnapshot.active !== promoSnapshot.active ? `${oldSnapshot.active} → ${promoSnapshot.active}` : 'unchanged',
              start_time: oldSnapshot.start_time !== promoSnapshot.start_time ? `${oldSnapshot.start_time} → ${promoSnapshot.start_time}` : 'unchanged',
              end_time: oldSnapshot.end_time !== promoSnapshot.end_time ? `${oldSnapshot.end_time} → ${promoSnapshot.end_time}` : 'unchanged',
              interval: oldSnapshot.interval !== promoSnapshot.interval ? `${oldSnapshot.interval} → ${promoSnapshot.interval}` : 'unchanged',
              duration: oldSnapshot.duration !== promoSnapshot.duration ? `${oldSnapshot.duration} → ${promoSnapshot.duration}` : 'unchanged'
            }
          });

          // Detectar mudança específica de PERMANENT para outro status
          if (oldSnapshot.modalStatus === 'PERMANENT' && promoSnapshot.modalStatus !== 'PERMANENT') {
            console.log(`❌ [VIRTUAL DOM] Detectada mudança crítica: PERMANENT → ${promoSnapshot.modalStatus} - fechando modal imediatamente`);
            setActivePromo(null);
            setShowPromoModal(false);
            setPromoTimerActive(false);
          }

          // FORÇA BYPASS IMEDIATO para qualquer mudança
          setIsRealtimeUpdate(true);

          // Limpar histórico específico da promoção para permitir nova exibição
          setPromoHistory(prevHistory => {
            const newHistory = new Map(prevHistory);
            newHistory.delete(promo.id);
            console.log(`🧹 [VIRTUAL DOM] Histórico da promoção ${promo.nome} limpo devido a mudanças`);
            return newHistory;
          });

          // Reset após 2 segundos para dar tempo da nova verificação
          setTimeout(() => setIsRealtimeUpdate(false), 2000);
        }
      }

      return newMap;
    });
  }, []);

  const canShowPromoBasedOnInterval = (promo: Promo): boolean => {
    const lastShown = promoHistory.get(promo.id);

    // Atualizar Virtual DOM
    updatePromoVirtualDOM(promo);

    // Se for uma atualização via Realtime, permitir bypass do intervalo
    if (isRealtimeUpdate) {
      console.log(`⚡ [PROMO INTERVAL] Bypass de intervalo ativo - promoção ${promo.nome} pode ser exibida imediatamente (via Realtime)`);
      return true;
    }

    // NOVA LÓGICA: Detectar mudança de status PERMANENT → TEMPORARY ou qualquer mudança
    // Se a promoção era PERMANENT antes e agora é TEMPORARY, permitir exibição imediata
    const wasShownAsPermanent = activePromo?.id === promo.id && activePromo['Modal visível'] === 'PERMANENT';
    const isNowTemporary = promo['Modal visível'] === 'TEMPORARY';
    const isNowHidden = promo['Modal visível'] === 'HIDDEN';

    if (wasShownAsPermanent && (isNowTemporary || isNowHidden)) {
      console.log(`🔄 [PROMO INTERVAL] Detectada mudança PERMANENT → ${promo['Modal visível']} para ${promo.nome} - forçando ação imediata`);

      // Se mudou para HIDDEN ou TEMPORARY, fechar o modal atual imediatamente
      if (isNowHidden || isNowTemporary) {
        console.log(`❌ [PROMO INTERVAL] Fechando modal permanente que mudou para ${promo['Modal visível']}`);
        setActivePromo(null);
        setShowPromoModal(false);
        setPromoTimerActive(false);

        // Se mudou para TEMPORARY, permitir nova exibição respeitando as regras
        if (isNowTemporary) {
          console.log(`🔄 [PROMO INTERVAL] Modal mudou para TEMPORARY - permitindo nova verificação`);
          return true;
        }

        // Se mudou para HIDDEN, não exibir
        if (isNowHidden) {
          console.log(`❌ [PROMO INTERVAL] Modal mudou para HIDDEN - não exibir`);
          return false;
        }
      }

      return true;
    }

    // Verificar se a promoção foi editada recentemente (Virtual DOM detectou mudanças)
    const virtualDOMSnapshot = promoVirtualDOM.get(promo.id);
    if (virtualDOMSnapshot && (Date.now() - virtualDOMSnapshot.timestamp < 5000)) { // 5 segundos
      console.log(`🔄 [PROMO INTERVAL] Edição recente detectada para ${promo.nome} - permitindo exibição imediata`);
      return true;
    }

    if (!lastShown) {
      console.log(`⏱️ [PROMO INTERVAL] Promoção ${promo.nome} - Primeira exibição permitida`);
      return true;
    }

    const now = new Date();
    const timeSinceLastShown = (now.getTime() - lastShown.getTime()) / (1000 * 60); // em minutos
    const intervalRequired = Number(promo.interval); // em minutos
    const canShow = timeSinceLastShown >= intervalRequired;

    console.log(`⏱️ [PROMO INTERVAL] Verificação de intervalo para ${promo.nome}:`, {
      lastShown: lastShown.toLocaleTimeString(),
      timeSinceLastShown: Math.round(timeSinceLastShown * 100) / 100, // 2 casas decimais
      intervalRequired,
      canShow,
      nextShowIn: canShow ? 0 : Math.round((intervalRequired - timeSinceLastShown) * 100) / 100
    });

    return canShow;
  };

  const shouldShowPromo = (promo: Promo): boolean => {
    console.log(`\n🎯 ===== VERIFICANDO PROMOÇÃO: ${promo.nome} =====`);
    console.log(`📊 [PROMO CHECK] Dados da promoção:`, {
      id: promo.id,
      nome: promo.nome,
      title: promo.title,
      active: promo.active,
      start_date: promo.start_date,
      end_date: promo.end_date,
      start_time: promo.start_time,
      end_time: promo.end_time,
      screen_ids: promo.screen_ids,
      currentScreenId: screenId,
      user_id: promo.user_id,
      currentUserId: userId,
      interval: promo.interval,
      duration: promo.duration,
      Typepromo: promo.Typepromo
    });

    // Verificar se a promoção está ativa
    if (!promo.active) {
      console.log(`❌ [PROMO CHECK] Promoção ${promo.nome} NÃO está ativa`);
      return false;
    }
    console.log(`✅ [PROMO CHECK] Promoção ${promo.nome} está ATIVA`);

    // Verificar se o modal está configurado para ser exibido
    const modalStatus = promo['Modal visível'];
    console.log(`🔍 [PROMO CHECK] Status do modal para ${promo.nome}: "${modalStatus}"`);

    if (modalStatus === 'HIDDEN') {
      console.log(`❌ [PROMO CHECK] Promoção ${promo.nome} tem modal OCULTO (HIDDEN)`);
      return false;
    }

    if (!modalStatus) {
      console.log(`❌ [PROMO CHECK] Promoção ${promo.nome} não tem status de modal definido`);
      return false;
    }

    console.log(`✅ [PROMO CHECK] Promoção ${promo.nome} tem modal configurado para exibição: ${modalStatus}`);

    // Verificar se está no período de data
    const dateCheck = isDateInRange(promo.start_date, promo.end_date);
    if (!dateCheck) {
      const hoje = new Date().toISOString().split('T')[0];
      console.log(`❌ [PROMO CHECK] Promoção ${promo.nome} FORA do período de data:`, {
        hoje: hoje,
        inicioPromo: promo.start_date,
        fimPromo: promo.end_date,
        motivo: hoje < promo.start_date ? 'Promoção ainda não começou' : 'Promoção já terminou'
      });
      return false;
    }
    console.log(`✅ [PROMO CHECK] Promoção ${promo.nome} DENTRO do período de data`);

    // Para promoções PERMANENT e LINKED, pular verificação de horário
    if (modalStatus === 'PERMANENT' || modalStatus === 'LINKED') {
      console.log(`🌟 [PROMO CHECK] Promoção ${promo.nome} é ${modalStatus} - pulando verificação de horário`);
    } else {
      // Verificar se está no horário (apenas para promoções TEMPORARY)
      console.log(`🕐 [PROMO CHECK] Verificando horário para promoção TEMPORARY: ${promo.nome}`);
      console.log(`🕐 [PROMO CHECK] Horários configurados - Início: ${promo.start_time}, Fim: ${promo.end_time}`);

      // Se os horários não estão definidos para TEMPORARY, considerar como válido
      if (!promo.start_time || !promo.end_time || promo.start_time === '' || promo.end_time === '') {
        console.log(`⚠️ [PROMO CHECK] Promoção TEMPORARY ${promo.nome} sem horários definidos - considerando válida`);
      } else {
        const timeCheck = isTimeInRange(promo.start_time, promo.end_time);
        if (!timeCheck) {
          console.log(`❌ [PROMO CHECK] Promoção ${promo.nome} FORA do horário`);
          console.log(`❌ [PROMO CHECK] RAZÃO: Horário atual não está entre ${promo.start_time} e ${promo.end_time}`);
          return false;
        }
        console.log(`✅ [PROMO CHECK] Promoção ${promo.nome} DENTRO do horário`);
      }
    }

    // Verificar se a tela está na lista de telas selecionadas
    const screenIds = Array.isArray(promo.screen_ids) ? promo.screen_ids : [];
    if (screenId && screenIds.length > 0 && !screenIds.includes(screenId)) {
      console.log(`❌ [PROMO CHECK] Promoção ${promo.nome} não configurada para a tela atual: ${screenId}`);
      console.log(`📱 [PROMO CHECK] Telas configuradas:`, screenIds);
      return false;
    }
    console.log(`✅ [PROMO CHECK] Promoção ${promo.nome} configurada para a tela atual`);

    // DEBUG: Ver os parâmetros recebidos
    console.log(`🔍 [DEBUG LINKED] Parâmetros recebidos no hook:`, {
      filteredMediasLength: filteredMedias?.length || 0,
      currentMediaIndex,
      hasFilteredMedias: !!filteredMedias,
      indexIsUndefined: currentMediaIndex === undefined
    });

    // VERIFICAÇÃO CRÍTICA PARA LINKED: Verificar se a mídia atual está selecionada
    if (modalStatus === 'LINKED') {
      let selectedMediaIds: string[] = [];
      const raw = promo.selected_media_ids as any;
      if (Array.isArray(raw)) {
        selectedMediaIds = raw.filter(v => typeof v === 'string');
      } else if (typeof raw === 'string') {
        const s = raw.trim();
        let parsed: string[] | null = null;
        if (s.startsWith('[') && s.endsWith(']')) {
          try { parsed = JSON.parse(s); } catch {}
        }
        if (!parsed && s.startsWith('{') && s.endsWith('}')) {
          const inner = s.slice(1, -1);
          parsed = inner.split(',').map(x => x.trim().replace(/^"|"$/g, '').replace(/^'|'$/g, ''));
        }
        if (!parsed) {
          parsed = s.split(',').map(x => x.trim()).filter(Boolean);
        }
        selectedMediaIds = (parsed || []).filter(v => typeof v === 'string');
      }

      // Obter mídia atual
      const indexValid = typeof currentMediaIndex === 'number' && currentMediaIndex >= 0 && filteredMedias && currentMediaIndex < (filteredMedias?.length || 0);
      const effectiveIndex = indexValid ? currentMediaIndex! : 0;
      const currentMedia = filteredMedias && (filteredMedias.length > 0)
        ? filteredMedias[effectiveIndex]
        : null;

      console.log(`🔍 [DEBUG LINKED] Calculando currentMedia:`, {
        filteredMediasLength: filteredMedias?.length || 0,
        currentMediaIndex,
        currentMediaCalculated: currentMedia?.id || 'null',
        currentMediaTitle: currentMedia?.title || 'null'
      });

      console.log(`🔗 [PROMO CHECK LINKED] Verificando mídia atual para promoção LINKED:`, {
        promoName: promo.nome,
        currentMediaId: currentMedia?.id || 'nenhuma',
        currentMediaTitle: currentMedia?.title || 'nenhuma',
        selectedMediaIds,
        totalSelected: selectedMediaIds.length
      });

      // Se não há mídia atual, não exibir
      if (!currentMedia) {
        console.log(`❌ [PROMO CHECK LINKED] Nenhuma mídia está tocando - promoção LINKED não será exibida`);
        return false;
      }

      // Se há mídias selecionadas, verificar se a atual está incluída
      if (selectedMediaIds.length > 0) {
        const isMediaSelected = selectedMediaIds.includes(currentMedia.id);

        if (!isMediaSelected) {
          console.log(`❌ [PROMO CHECK LINKED] Mídia atual '${currentMedia.title}' (${currentMedia.id}) NÃO está nas mídias selecionadas`);
          console.log(`❌ [PROMO CHECK LINKED] Mídias configuradas:`, selectedMediaIds);
          console.log(`❌ [PROMO CHECK LINKED] Promoção LINKED ${promo.nome} NÃO será exibida`);
          return false;
        }

        console.log(`✅ [PROMO CHECK LINKED] Mídia atual '${currentMedia.title}' está selecionada para esta promoção`);
      } else {
        console.log(`⚠️ [PROMO CHECK LINKED] Nenhuma mídia específica selecionada - promoção será exibida em todas as mídias`);
      }
    }

    // Para promoções PERMANENT e LINKED, pular verificação de intervalo
    if (modalStatus === 'PERMANENT' || modalStatus === 'LINKED') {
      console.log(`🌟 [PROMO CHECK] Promoção ${promo.nome} é ${modalStatus} - pulando verificação de intervalo`);
    } else {
      // Verificar intervalo entre exibições (apenas para promoções TEMPORARY)
      console.log(`⏱️ [PROMO CHECK] Verificando intervalo para promoção TEMPORARY: ${promo.nome}`);
      console.log(`⏱️ [PROMO CHECK] Intervalo configurado: ${promo.interval} minutos`);

      const intervalCheck = canShowPromoBasedOnInterval(promo);
      if (!intervalCheck) {
        console.log(`❌ [PROMO CHECK] Promoção ${promo.nome} ainda no intervalo de espera`);
        console.log(`❌ [PROMO CHECK] RAZÃO: Intervalo de ${promo.interval} minutos ainda não passou desde última exibição`);
        return false;
      }
      console.log(`✅ [PROMO CHECK] Promoção ${promo.nome} pode ser exibida (intervalo OK)`);
    }

    console.log(`🎉 [PROMO CHECK] RESULTADO: Promoção ${promo.nome} DEVE SER EXIBIDA!`);
    console.log(`🎯 ===== FIM VERIFICAÇÃO: ${promo.nome} =====\n`);
    return true;
  };

  const checkPromotions = useCallback(async () => {
    if (!userId) {
      console.log('❌ usePromotions - UserId não fornecido');
      return;
    }

    try {
      console.log('\n🎯 [PROMO FETCH] ===== INICIANDO VERIFICAÇÃO =====');
      console.log('🎯 [PROMO FETCH] Verificando promoções para userId:', userId);
      console.log('🎯 [PROMO FETCH] ScreenId atual:', screenId);
      console.log('🕐 [PROMO FETCH] Hora atual:', new Date().toLocaleTimeString('pt-BR'));
      console.log('📅 [PROMO FETCH] Data atual:', new Date().toLocaleDateString('pt-BR'));
      console.log('📊 [PROMO FETCH] Estado atual - activePromo:', activePromo?.nome || 'nenhuma');
      console.log('📊 [PROMO FETCH] Estado atual - promoHistory:', Array.from(promoHistory.entries()).map(([id, date]) => [id, date.toLocaleTimeString()]));

      const { data: promos, error } = await supabase
        .from('promos')  // ✅ CORRIGIDO: tabela correta
        .select('*, "Modal visível", campaign_id')
        .eq('user_id', userId)
        .eq('active', true);

      if (error) {
        console.error('❌ Erro ao buscar promoções:', error);
        return;
      }

      console.log('📋 [PROMO FETCH] Promoções encontradas no banco:', promos?.length || 0);

      // Log detalhado de cada promoção
      if (promos && promos.length > 0) {
        promos.forEach((promo, index) => {
          console.log(`🔍 [PROMO FETCH] Promoção ${index + 1}:`, {
            id: promo.id,
            nome: promo.nome,
            title: promo.title,
            start_date: promo.start_date,
            end_date: promo.end_date,
            start_time: promo.start_time,
            end_time: promo.end_time,
            active: promo.active,
            screen_ids: promo.screen_ids,
            interval: promo.interval,
            duration: promo.duration,
            'Modal visível': promo['Modal visível']
          });
        });
      } else {
        console.log('⚠️ [PROMO FETCH] Nenhuma promoção encontrada para o usuário');
      }

      // Encontrar promoção que deve ser exibida agora
      // Priorizar promoções PERMANENT > LINKED > TEMPORARY
      const validPromos = promos?.filter(promo => shouldShowPromo(promo)) || [];
      const permanentPromos = validPromos.filter(p => p['Modal visível'] === 'PERMANENT');
      const linkedPromos = validPromos.filter(p => p['Modal visível'] === 'LINKED');
      const temporaryPromos = validPromos.filter(p => p['Modal visível'] === 'TEMPORARY');

      // Dar prioridade: PERMANENT > LINKED > TEMPORARY
      const promoToShow = permanentPromos.length > 0
        ? permanentPromos[0]
        : linkedPromos.length > 0
          ? linkedPromos[0]
          : temporaryPromos[0];

      if (promoToShow) {
        console.log(`🎯 [PROMO SELECTION] Promoção selecionada: ${promoToShow.nome} (Tipo: ${promoToShow['Modal visível']})`);
      }

      if (promoToShow) {
        // Sistema Virtual DOM já detectou mudanças automaticamente
        // Forçar bypass para QUALQUER promoção válida se houve mudanças recentes
        const recentChange = promoVirtualDOM.get(promoToShow.id);
        if (recentChange && (Date.now() - recentChange.timestamp < 10000)) { // 10 segundos
          console.log('🚀 [VIRTUAL DOM] Mudança recente detectada - forçando exibição imediata');
          setIsRealtimeUpdate(true);
          setTimeout(() => setIsRealtimeUpdate(false), 1000);
        }

        // Se for a mesma promoção mas com horário diferente, permitir atualização
        const isSamePromoButUpdated = promoToShow.id === activePromo?.id;

        if (isSamePromoButUpdated) {
          console.log('🎯 [PROMO RESULT] ✅ ATUALIZANDO MESMA PROMOÇÃO COM NOVOS DADOS:', promoToShow.nome);
          console.log('🎯 [PROMO RESULT] Horário pode ter mudado, atualizando exibição');
        } else if (promoToShow.id !== activePromo?.id) {
          console.log('🎯 [PROMO RESULT] ✅ EXIBINDO NOVA PROMOÇÃO:', promoToShow.nome);
        }

        console.log('🎯 [PROMO RESULT] Dados da promoção a ser exibida:', {
          id: promoToShow.id,
          nome: promoToShow.nome,
          title: promoToShow.title,
          Typepromo: promoToShow.Typepromo,
          duration: promoToShow.duration,
          start_time: promoToShow.start_time,
          end_time: promoToShow.end_time,
          isSamePromoButUpdated
        });

        setActivePromo(promoToShow);
        setShowPromoModal(true);
        setPromoTimerActive(promoToShow['Modal visível'] === 'TEMPORARY');
        // Registrar o momento da exibição para controle de intervalo
        setPromoHistory(prev => new Map(prev.set(promoToShow.id, new Date())));
      } else if (!promoToShow && activePromo) {
        const activeStatus = activePromo['Modal visível'];
        if (activeStatus === 'LINKED' || !promoTimerActive) {
          setActivePromo(null);
          setShowPromoModal(false);
          setPromoTimerActive(false);
        }
      } else if (!promoToShow) {
        console.log('🎯 [PROMO RESULT] Nenhuma promoção para exibir no momento');
      }

      setLastCheck(new Date());
      console.log('🎯 [PROMO FETCH] ===== FIM VERIFICAÇÃO =====\n');
    } catch (error) {
      console.error('❌ Erro ao verificar promoções:', error);
    }
  }, [userId, screenId, activePromo, filteredMedias, currentMediaIndex]);

  const closePromoModal = useCallback(() => {
    console.log('🔴 [PROMO CLOSE] Fechando modal de promoção');
    setShowPromoModal(false);
    setActivePromo(null);
    setPromoTimerActive(false);
    console.log('🔴 [PROMO CLOSE] Timer de promoção desativado');
  }, []);

  // Função para limpar histórico de uma promoção específica (útil após edição)
  const clearPromoHistory = useCallback((promoId?: string) => {
    if (promoId) {
      console.log(`🧹 [PROMO CLEAR] Limpando histórico da promoção ${promoId}`);
      setPromoHistory(prev => {
        const newHistory = new Map(prev);
        newHistory.delete(promoId);
        return newHistory;
      });
    } else {
      console.log('🧹 [PROMO CLEAR] Limpando histórico completo de promoções');
      setPromoHistory(new Map());
    }
  }, []);

  // Função para forçar verificação imediata (útil para testes)
  const forceCheckPromotions = useCallback(() => {
    console.log('🔄 [PROMO FORCE] Forçando verificação imediata de promoções');
    console.log('🔄 [PROMO FORCE] Estado atual:', {
      userId,
      screenId,
      activePromo: activePromo?.nome || 'nenhuma',
      showPromoModal,
      promoTimerActive
    });
    checkPromotions();
  }, [checkPromotions, userId, screenId, activePromo, showPromoModal, promoTimerActive]);

  // Verificar promoções a cada 10 segundos (mais responsivo para testes)
  useEffect(() => {
    if (!userId) {
      console.log('❌ [PROMO EFFECT] UserId não fornecido, não verificando promoções');
      // Limpar estado se não houver userId
      setActivePromo(null);
      setShowPromoModal(false);
      setPromoTimerActive(false);
      return;
    }

    console.log('🚀 [PROMO EFFECT] Iniciando verificação de promoções', {
      userId,
      screenId,
      timestamp: new Date().toLocaleTimeString('pt-BR')
    });

    // Verificação inicial
    checkPromotions();

    // ===== SUPABASE REALTIME LISTENER =====
    // Configurar listener para mudanças reais no banco de dados
    let promoChannel: any = null;

    if (userId) {
      const channelId = `promo-realtime-${userId}-${Date.now()}`;
      promoChannel = supabase
        .channel(channelId)
        .on(
          'postgres_changes',
          {
            event: '*', // Listen for all events (INSERT, UPDATE, DELETE)
            schema: 'public',
            table: 'promos',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('🔄 [SUPABASE REALTIME] Mudança detectada na tabela promos:', payload);
            console.log('🔄 [SUPABASE REALTIME] Tipo de evento:', payload.eventType);
            console.log('🔄 [SUPABASE REALTIME] Dados antigos:', payload.old);
            console.log('🔄 [SUPABASE REALTIME] Dados novos:', payload.new);

            // Se foi uma atualização e mudou o campo "Modal visível"
            if (payload.eventType === 'UPDATE' && payload.old && payload.new) {
              const oldModalStatus = payload.old['Modal visível'];
              const newModalStatus = payload.new['Modal visível'];

              if (oldModalStatus !== newModalStatus) {
                console.log(`🔄 [SUPABASE REALTIME] Status do modal mudou de "${oldModalStatus}" para "${newModalStatus}"`);
                console.log(`🔄 [SUPABASE REALTIME] Promoção ID: ${payload.new.id}, Nome: ${payload.new.nome}`);

                // IMPORTANTE: Para qualquer mudança de status, limpar histórico específico da promoção
                console.log('🔄 [SUPABASE REALTIME] Mudança de status detectada - limpando histórico específico para permitir nova exibição');
                clearPromoHistory(payload.new.id);

                // Emitir evento customizado para atualização imediata
                window.dispatchEvent(new CustomEvent('promotionUpdated', {
                  detail: {
                    promoId: payload.new.id,
                    action: 'modal_status_changed',
                    oldStatus: oldModalStatus,
                    newStatus: newModalStatus,
                    timestamp: new Date().toISOString()
                  }
                }));
              }
            }

            // Para qualquer tipo de mudança (INSERT, UPDATE, DELETE), verificar promoções
            console.log('🔄 [SUPABASE REALTIME] Verificando promoções após mudança no banco...');

            // IMPORTANTE: Resetar o timer se estiver ativo para permitir nova exibição
            if (promoTimerActive) {
              console.log('🔄 [SUPABASE REALTIME] Timer ativo detectado, resetando para permitir nova exibição');
              setPromoTimerActive(false);
            }

            // Forçar reset completo do estado quando há mudanças no banco
            console.log('🔄 [SUPABASE REALTIME] Resetando estado completo para mudanças imediatas');
            setActivePromo(null);
            setShowPromoModal(false);

            // Limpar histórico e verificar novamente após um pequeno delay
            clearPromoHistory();

            // Usar setTimeout para garantir que o estado seja resetado antes da nova verificação
            setTimeout(() => {
              console.log('🔄 [SUPABASE REALTIME] Executando nova verificação após reset do estado');
              setIsRealtimeUpdate(true); // Ativar bypass de intervalo
              checkPromotions();
              // Resetar flag após verificação
              setTimeout(() => setIsRealtimeUpdate(false), 1000);
            }, 100);
          }
        )
        .subscribe();
    }

    // Adicionar listener para evento de atualização de promoção (via JavaScript)
    const handlePromotionUpdate = (event: CustomEvent) => {
      console.log('🔄 [PROMO EFFECT] Evento de atualização de promoção recebido, verificando imediatamente...');
      console.log('🔄 [PROMO EFFECT] Detalhes do evento:', event.detail);
      console.log('🔄 [PROMO EFFECT] Estado atual antes da verificação:', {
        activePromo: activePromo?.nome || 'nenhuma',
        showPromoModal,
        promoTimerActive,
        promoHistorySize: promoHistory.size,
        historyEntries: Array.from(promoHistory.entries()).map(([id, date]) => [id, date.toLocaleTimeString()])
      });

      // IMPORTANTE: Resetar o timer se estiver ativo para permitir nova exibição
      if (promoTimerActive) {
        console.log('🔄 [PROMO EFFECT] Timer ativo detectado, resetando para permitir nova exibição');
        setPromoTimerActive(false);
      }

      // Se tivermos o ID da promoção específica, limpar apenas ela
      if (event.detail?.promoId) {
        console.log(`🔄 [PROMO EFFECT] Limpando histórico da promoção específica: ${event.detail.promoId}`);
        clearPromoHistory(event.detail.promoId);
      } else {
        // Limpar histórico completo quando uma promoção é atualizada para permitir nova exibição
        console.log('🔄 [PROMO EFFECT] Limpando histórico completo de promoções após atualização');
        clearPromoHistory();
      }

      checkPromotions();
    };

    window.addEventListener('promotionUpdated', handlePromotionUpdate);

    // Verificar a cada 5 segundos (otimizado - menos agressivo que 2 segundos)
    const interval = setInterval(() => {
      console.log('🔍 [VIRTUAL DOM] Verificação periódica de mudanças...');
      checkPromotions();
    }, 5000);
    console.log('⏰ [PROMO EFFECT] Timer configurado para verificar a cada 5 segundos');

    // Polling DEBUG reduzido para 3 segundos (menos agressivo)
    const debugInterval = setInterval(() => {
      if (userId && screenId) {
        console.log('🚫 [DEBUG FORCE] Verificação forçada a cada 3 segundos...');
        checkPromotions();
      }
    }, 3000);

    return () => {
      console.log('🛑 [PROMO EFFECT] Limpando timer de verificação de promoções e listeners');
      clearInterval(interval);
      clearInterval(debugInterval);
      window.removeEventListener('promotionUpdated', handlePromotionUpdate);
      if (promoChannel) {
        console.log('🔌 [PROMO] Removendo canal Realtime...');
        supabase.removeChannel(promoChannel);
        promoChannel = null;
      }
    };
  }, [userId, screenId, activePromo, showPromoModal, promoTimerActive, promoHistory, checkPromotions, clearPromoHistory]);

  useEffect(() => {
    if (!userId || !screenId) return;

    console.log(`🔄 [MEDIA CHANGE] Mudança de mídia detectada:`, {
      currentMediaIndex,
      hasFilteredMedias: !!filteredMedias,
      filteredMediasLength: filteredMedias?.length || 0
    });

    if (activePromo && activePromo['Modal visível'] === 'LINKED') {
      let ids: string[] = [];
      const raw = activePromo.selected_media_ids as any;
      if (Array.isArray(raw)) {
        ids = raw.filter((v: any) => typeof v === 'string');
      } else if (typeof raw === 'string') {
        const s = raw.trim();
        let parsed: string[] | null = null;
        if (s.startsWith('[') && s.endsWith(']')) {
          try { parsed = JSON.parse(s); } catch {}
        }
        if (!parsed && s.startsWith('{') && s.endsWith('}')) {
          const inner = s.slice(1, -1);
          parsed = inner.split(',').map(x => x.trim().replace(/^"|"$/g, '').replace(/^'|'$/g, ''));
        }
        if (!parsed) {
          parsed = s.split(',').map(x => x.trim()).filter(Boolean);
        }
        ids = (parsed || []).filter(v => typeof v === 'string');
      }
      const indexValid = typeof currentMediaIndex === 'number' && currentMediaIndex >= 0 && filteredMedias && currentMediaIndex < (filteredMedias?.length || 0);
      const effectiveIndex = indexValid ? currentMediaIndex! : 0;
      const current = filteredMedias && (filteredMedias.length > 0) ? filteredMedias[effectiveIndex] : null;
      const selected = current ? ids.includes(current.id) : false;
      if (!selected) {
        setShowPromoModal(false);
        setActivePromo(null);
        setPromoTimerActive(false);
      }
    }

    checkPromotions();
  }, [currentMediaIndex, filteredMedias, userId, screenId, activePromo, checkPromotions]);

  return {
    activePromo,
    showPromoModal,
    closePromoModal,
    forceCheckPromotions,
    clearPromoHistory,
    isLoading: false
  };
};
